/**
 * The page's side of the service worker (src/sw/sw.ts): registration, the
 * "showing saved copies" state, and emptying the saved copies on sign-out.
 */

import { readonly, ref } from 'vue'

/** When the oldest saved copy on screen was stored; null while live. */
const savedAt = ref<string | null>(null)

interface WorkerMessage {
  source: 'memory-sw'
  cachedAt: string | null
}

function isWorkerMessage(data: unknown): data is WorkerMessage {
  return (data as WorkerMessage | null)?.source === 'memory-sw'
}

/**
 * How often the open app asks again. The worker's 8-minute limit decides, so a
 * run lands at most a minute late.
 */
const PREFETCH_TICK_MS = 60_000

export function startServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return
  if (import.meta.env.VITE_SW === 'off') {
    // The kill-switch build: the served sw.js removes itself; make sure
    // nothing puts it back.
    void navigator.serviceWorker
      .getRegistrations()
      .then((all) => Promise.all(all.map((r) => r.unregister())))
    return
  }
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (!isWorkerMessage(e.data)) return
    const at = e.data.cachedAt
    savedAt.value = at === null ? null : savedAt.value && savedAt.value < at ? savedAt.value : at
  })
  // On the first launch after install, the page's reads go out before the
  // worker takes control, so none of them is saved and the first offline launch
  // would find nothing. Replay them once it is in control.
  if (!navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', replayEarlyReads, { once: true })
  }
  void navigator.serviceWorker.register('/sw.js').catch(() => {
    /* no worker: the app simply works online-only */
  })
  // Save the newest and starred links now, whenever the app comes back to the
  // foreground, and every minute while it stays there. The worker runs at
  // most every 8 minutes and skips what is current, so a tick that is not due
  // costs one message and one cache lookup.
  let tick: ReturnType<typeof setInterval> | undefined
  const onVisibility = () => {
    clearInterval(tick)
    if (document.visibilityState !== 'visible') return
    requestPrefetch()
    tick = setInterval(requestPrefetch, PREFETCH_TICK_MS)
  }
  onVisibility()
  document.addEventListener('visibilitychange', onVisibility)
}

function requestPrefetch() {
  void navigator.serviceWorker.ready.then((reg) =>
    reg.active?.postMessage({ source: 'memory-page', type: 'prefetch' })
  )
}

/**
 * The same-origin API reads the page has made so far, from its resource
 * timings. Each is replayed as a GET; the worker decides which replies to keep.
 */
export function earlyReads(
  entries: Pick<PerformanceResourceTiming, 'name' | 'initiatorType'>[],
  origin: string
): string[] {
  const urls = new Set<string>()
  for (const e of entries) {
    if (e.initiatorType !== 'fetch') continue
    const url = new URL(e.name)
    if (url.origin === origin && url.pathname.startsWith('/api/')) urls.add(url.href)
  }
  return [...urls]
}

function replayEarlyReads() {
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
  for (const url of earlyReads(entries, location.origin)) {
    void fetch(url, { credentials: 'same-origin' }).catch(() => {})
  }
}

/** Every saved copy goes: the library holds private documents in full. */
export async function clearSavedCopies(): Promise<void> {
  if (!('caches' in window)) return
  for (const name of await caches.keys()) await caches.delete(name)
}

export function useOffline() {
  return { savedAt: readonly(savedAt) }
}
